import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createWorkflowTeam, editWorkflowTeam } from "@/hooks/useLocalStorage";
import { WorkflowTeamsRes } from "@/lib/api-services";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Loader2 } from "lucide-react";


export type TeamFormValues = {
  teamName: string;
  teamDesc: string;
  workflowAssignment: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: WorkflowTeamsRes | null;
  onAlert: (message: string, status: boolean) => void;
};


const WORKFLOW_ASSIGNMENT_OPTIONS = [
  { label: "Round Robin", value: "round_robin" },
  { label: "Direct Assign", value: "direct_assign" },
];

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

export function AddWorkflowPerformerTeam({
  open,
  onOpenChange,
  initialData,
  onAlert
}: Props) {
  const isEdit = !!initialData;

  const addWorkflowTeams = createWorkflowTeam();
  const editWorkflowTeams = editWorkflowTeam();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TeamFormValues>({
    defaultValues: {
      teamName: "",
      teamDesc: "",
      workflowAssignment: "",
    },
  });

  useEffect(() => {
    if (!open) return;

    if (isEdit && initialData) {
      reset(initialData);
    } else {
      reset({
        teamName: "",
        teamDesc: "",
        workflowAssignment: "",
      });
    }
  }, [open, initialData]);

  const onSubmit = (data: TeamFormValues) => {
    if (isEdit) {
      editWorkflowTeams.mutate({ Id: initialData.id, data: data });
    } else {
      addWorkflowTeams.mutate(data);
    }
  };

  useEffect(() => {
    if (addWorkflowTeams.isSuccess) {
      onOpenChange(false);
      onAlert("Workflow Performer Roles Created Successfully!", true);
      addWorkflowTeams.reset();
    } else if (addWorkflowTeams.isError) {
      const raw = addWorkflowTeams.error as Error;
      onOpenChange(false);
      onAlert(parseApiErrorMessage(raw.message), false);
      addWorkflowTeams.reset();
    }
  }, [addWorkflowTeams.isSuccess, addWorkflowTeams.isError]);

  useEffect(() => {
    if (editWorkflowTeams.isSuccess) {
      onOpenChange(false);
      onAlert("Workflow Performer Roles Updated Successfully!", true);
      editWorkflowTeams.reset();
    } else if (editWorkflowTeams.isError) {
      const raw = editWorkflowTeams.error as Error;
      onOpenChange(false);
      onAlert(parseApiErrorMessage(raw.message), false);
      editWorkflowTeams.reset();
    }
  }, [editWorkflowTeams.isSuccess, editWorkflowTeams.isError]);

  const isPending = addWorkflowTeams.isPending || editWorkflowTeams.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Team" : "Add Team"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

          <Field label="Team Name" required>
            <Input
              placeholder="Enter team name"
              {...register("teamName", { required: "Team name is required" })}
            />
            {errors.teamName && (
              <p className="text-red-500 text-xs mt-1">
                {errors.teamName.message}
              </p>
            )}
          </Field>

          <Field label="Team Description">
            <Textarea
              placeholder="Enter description"
              className="min-h-[100px]"
              {...register("teamDesc")}
            />
          </Field>

          <Field label="Workflow Assignment" required>
            <Controller
              name="workflowAssignment"
              control={control}
              rules={{ required: "Please select a workflow assignment" }}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignment type" />
                  </SelectTrigger>
                  <SelectContent>
                    {WORKFLOW_ASSIGNMENT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.workflowAssignment && (
              <p className="text-red-500 text-xs mt-1">
                {errors.workflowAssignment.message}
              </p>
            )}
          </Field>

          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? "Update" : "Save"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}